import { useQuery } from '@tanstack/react-query';

import { BarChart } from '../../components/charts/BarChart.jsx';
import { ChartCard } from '../../components/charts/ChartCard.jsx';
import { DonutChart } from '../../components/charts/DonutChart.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { MetricCard } from '../../components/ui/Card.jsx';
import { EmptyState } from '../../components/ui/EmptyState.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { projectsApi } from '../../services/projectsApi.js';
import { formatCurrency, formatNumber } from '../../utils/formatters.js';
import { toBudgetSeries, toProjectStatusSeries, toTaskStatusSeries } from './projectCharts.js';

const sum = (arr) => arr.reduce((a, b) => a + b, 0);

export function ProjectsDashboardPage() {
  const { t } = useTranslation();
  const palette = useChartPalette();

  const statusBreakdown = useQuery({ queryKey: ['projects', 'status-breakdown'], queryFn: () => projectsApi.statusBreakdown().then((r) => r.items) });
  const budget = useQuery({ queryKey: ['projects', 'budget-vs-actual'], queryFn: () => projectsApi.budgetVsActual().then((r) => r.items) });
  const taskStatus = useQuery({ queryKey: ['projects', 'task-status'], queryFn: () => projectsApi.taskStatus().then((r) => r.items) });

  const statusSeries = toProjectStatusSeries(statusBreakdown.data?.statuses);
  const budgetSeries = toBudgetSeries(budget.data?.projects);
  const taskSeries = toTaskStatusSeries(taskStatus.data?.statuses);

  const activeProjects = (statusBreakdown.data?.statuses || []).find((s) => s.status === 'ACTIVE')?.count || 0;
  const totalBudget = sum(budgetSeries.budget);
  const totalActual = sum(budgetSeries.actual);
  const openTasks = (taskStatus.data?.statuses || [])
    .filter((s) => s.status !== 'DONE')
    .reduce((a, s) => a + (Number(s.count) || 0), 0);

  const accent = palette?.accent || '#1d4ed8';
  const expense = palette?.expense || '#dc2626';

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('projects.dashboard.title')}</h1>
          <p className="muted">{t('projects.dashboard.subtitle')}</p>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('projects.metric.active')} value={formatNumber(activeProjects, 0)} />
        <MetricCard label={t('projects.metric.budget')} value={formatCurrency(totalBudget)} />
        <MetricCard label={t('projects.metric.actual')} value={formatCurrency(totalActual)} />
        <MetricCard label={t('projects.metric.openTasks')} value={formatNumber(openTasks, 0)} trendVariant="success" />
      </div>

      <div className="dashboard-grid">
        <ChartCard className="span-2" title={t('projects.chart.budget')} subtitle={t('projects.chart.budgetSub')}>
          {budget.isLoading ? <Spinner /> : budgetSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart
              labels={budgetSeries.labels}
              datasets={[
                { label: t('projects.chart.budgetPlanned'), data: budgetSeries.budget, color: accent },
                { label: t('projects.chart.budgetActual'), data: budgetSeries.actual, color: expense },
              ]}
            />
          )}
        </ChartCard>

        <ChartCard title={t('projects.chart.status')}>
          {statusBreakdown.isLoading ? <Spinner /> : statusSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <DonutChart labels={statusSeries.labels} values={statusSeries.counts} />
          )}
        </ChartCard>

        <ChartCard title={t('projects.chart.tasks')}>
          {taskStatus.isLoading ? <Spinner /> : taskSeries.labels.length === 0 ? <EmptyState message={t('common.noData')} /> : (
            <BarChart labels={taskSeries.labels} datasets={[{ label: t('projects.chart.taskCount'), data: taskSeries.counts, color: accent }]} />
          )}
        </ChartCard>
      </div>
    </section>
  );
}
